import { api } from "@/redux/api";

import { BlacklistRequestPaginationParams } from "@/types/blacklist-request-params";

import {
	Blacklist,
	BlacklistResponse,
	BlacklistResponseSchema,
	BlacklistSchema,
	BlacklistUploadResult,
	BlacklistUploadResultSchema,
	CreateBlacklist,
} from "../../pages/Blacklist/schema/blacklist";

export const blacklistApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getBlacklists: builder.query<BlacklistResponse, BlacklistRequestPaginationParams>({
			query: (params) => ({
				url: "blacklist",
				params: {
					...(params && { ...params }),
				},
				responseSchema: BlacklistResponseSchema,
			}),
			providesTags: ["Blacklist"],
		}),
		getBlacklist: builder.query<Blacklist, number>({
			query: (id: number) => ({
				url: `blacklist/${id}`,
				params: {
					id,
				},
				responseSchema: BlacklistSchema,
			}),
			providesTags: ["Blacklist"],
		}),
		createBlacklist: builder.mutation<Blacklist | Blacklist[], CreateBlacklist>({
			query: (body) => ({
				url: `blacklist`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["Blacklist"],
		}),
		// The Inbox's "Unsubscribe" (PREC-2548): the same record the lead's own unsubscribe link
		// writes, so it is an unsubscribe, not a user blacklist entry. listId refreshes that list.
		unsubscribeLead: builder.mutation<
			{ listPersonId: number },
			{ listPersonId: number; listId: number }
		>({
			query: ({ listPersonId }) => ({
				url: `unsubscribe/lead/${listPersonId}`,
				method: "POST",
			}),
			invalidatesTags: (result, error, arg) => [
				"Blacklist",
				{ type: "List", id: arg.listId },
				"ListPerson",
			],
		}),
		uploadBlacklist: builder.mutation<BlacklistUploadResult, FormData>({
			query: (body) => ({
				url: `blacklist/upload`,
				method: "POST",
				body,
				responseSchema: BlacklistUploadResultSchema,
			}),
			invalidatesTags: ["Blacklist"],
		}),
		updateBlacklist: builder.mutation<Blacklist, Partial<Blacklist>>({
			query: ({ id, ...body }) => ({
				url: `blacklist/${id}`,
				method: "PATCH",
				body,
				responseSchema: BlacklistSchema,
			}),
			invalidatesTags: ["Blacklist"],
		}),
		deleteBlacklist: builder.mutation<void, number>({
			query: (id) => ({
				url: `blacklist/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Blacklist"],
		}),
	}),
});

export const {
	useGetBlacklistsQuery,
	useLazyGetBlacklistsQuery,
	useGetBlacklistQuery,
	useUpdateBlacklistMutation,
	useDeleteBlacklistMutation,
	useUploadBlacklistMutation,
	useCreateBlacklistMutation,
	useUnsubscribeLeadMutation,
} = blacklistApi;
export const accountApiReducer = blacklistApi.reducer;
export const accountApiMiddleware = blacklistApi.middleware;
